import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { centralizedHost } from "~/Utils";

export interface BaggageGuide {
  id: number;
  title: string;
  image: string;
  field_page_title_en: string;
  field_page_title_ms: string;
  field_page_title_zh: string;
  field_content_description_en: string;
  field_content_description_ms: string;
  field_content_description_zh: string;
  description: string;
  pageName: string;
  displayPath: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
}
export interface SingleCabinLuggagePolicySection1 {
  id: string;
  title: string;
  title_malay: string;
  title_mandarin: string;
  description_english_1: string;
  description_malay_1: string;
  description_mandarin_1: string;
  description_english_2: string;
  description_malay_2: string;
  description_mandarin_2: string;
  image: string;
  description_english_3: string;
  description_malay_3: string;
  description_mandarin_3: string;
}
export interface SingleCabinLuggagePolicySection2 {
  title: string;
  image: string;
  id: string;
  title_mandarin: string;
  title_malay: string;
  updatedAt: string;
  createdAt: string;
  published: any;
  created: string;
  changed: string;
}
export interface GuidelinesForCheckedinLuggageSection1 {
  id: string;
  title: string;
  title_mandarin: string;
  title_malay: string;
  tab_title_english: string;
  tab_title_malay: string;
  tab_title_mandarin: string;
}

export interface GuidelinesForCheckedinLuggageSection2 {
  title: string;
  image: string;
  id: string;
  title_mandarin: string;
  title_malay: string;
  updatedAt: string;
  createdAt: string;
  published: string;
}

export interface RestrictedItemsSections {
  nid: string;
  id: any;
  english_title: string;
  malay_title: string;
  mandarin_title: string;
  english_title_2: string;
  malay_title_2: string;
  mandarin_title_2: string;
  english_title_3: string;
  malay_title_3: string;
  mandarin_title_3: string;
  tab_title_english: string;
  tab_title_malay: string;
  tab_title_mandarin: string;
  description_mandarin_2: string;
  description_malay_2: string;
  description_english_2: string;
  description_mandarin_1: string;
  description_malay_1: string;
  body: string;
  title_mandarin: string;
  title_malay: string;
  title: string;
}
export interface LagsSection3 {
  english_title: string;
  description_english_1: string;
  description_malay_1: string;
  description_mandarin_1: string;
  download_button_text_english: string;
  download_button_text_mandarin: string;
  download_button_text_malay: string;
  malay_title: string;
  mandarin_title: string;
  nid: string;
  id: any;
  title: string;
  title_malay: string;
  title_mandarin: string;
  button_text_english: string;
  button_text_malay: string;
  button_text_mandarin: string;
  body: string;
  pdf_url: string;
}
export interface LagsSection1 {
  id: any;
  nid: string;
  title_mandarin: string;
  title: string;
  title_malay: string;
  english_title: string;
  malay_title: string;
  mandarin_title: string;
  description_english_1: string;
  description_malay_1: string;
  description_mandarin_1: string;
  description_english_2: string;
  description_malay_2: string;
  description_mandarin_2: string;
  image: string;
  description_english_3: string;
  description_malay_3: string;
  description_mandarin_3: string;
  description_english_4: string;
  description_malay_4: string;
  description_mandarin_4: string;
  description_english_5: string;
  description_malay_5: string;
  description_mandarin_5: string;
  tab_title_english: string;
  tab_title_malay: string;
  tab_title_mandarin: string;
  body: string;
}
export interface LagsSection4 {
  nid: string;
  description_english: string;
  description_malay: string;
  description_mandarin: string;
  description_english_2: string;
  description_malay_2: string;
  description_mandarin_2: string;
  description_english_3: string;
  description_malay_3: string;
  description_mandarin_3: string;
  id: any;
  body: string;
  description_malay_1: string;
  description_mandarin_1: string;
}

export interface LagsGuidelineSection {
  id: string;
  description_en: string;
  description_malay: string;
  description_mandarin: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

export interface LagsSteps {
  id: string;
  description_en: string;
  description_malay: string;
  description_mandarin: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

const fetchData = async <T>(url: string): Promise<T> => {
  const response = await axios.get<T>(url);
  return response.data;
};

export const createDataHook =
  <T>(key: string, url: string) =>
  (
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key],
        enabled: false,
        queryFn: () => fetchData<T>(url),
        refetchOnWindowFocus: false,
        ...options,
      },
      queryClient
    );
  };

export const createDataHooksForPages =
  <T>(key: string, getUrl: (listId: string) => string) =>
  (
    listId: string,
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key, listId],
        enabled: false,
        queryFn: () => fetchData<T>(getUrl(listId)),
        refetchOnWindowFocus: false,
        ...options,
      },
      queryClient
    );
  };

const useSingleCabinLuggagePolicySection1 = createDataHook<SingleCabinLuggagePolicySection1[]>(
  "singleCabinLuggagePolicySection1",
  `${centralizedHost}/api/baggage/single-cabin/section-one`
);
const useSingleCabinLuggagePolicySection2 = createDataHooksForPages<SingleCabinLuggagePolicySection2[]>(
  "singleCabinLuggagePolicySection2",
  (listId) => `${centralizedHost}/api/baggage/single-cabin/section-two/${listId}`
);
const useGuidelinesForCheckedinLuggageSection1 = createDataHook<GuidelinesForCheckedinLuggageSection1[]>(
  "guidelinesForCheckedinLuggageSection1",
  `${centralizedHost}/api/baggage/checked-in/section-one`
);
const useGuidelinesForCheckedinLuggageSection2 = createDataHooksForPages<GuidelinesForCheckedinLuggageSection2[]>(
  "guidelinesForCheckedinLuggageSection2",
  (listId) => `${centralizedHost}/api/baggage/checked-in/section-two/${listId}`
);
const useLagsSection1 = createDataHook<LagsSection1[]>("lagsSection1", `${centralizedHost}/api/lags/section-one`);
const useLagsSection2 = createDataHooksForPages<SingleCabinLuggagePolicySection2[]>(
  "lagsSection2",
  (listId) => `${centralizedHost}/api/lags/section-two/${listId}`
);
const useRestrictedItemsSections = createDataHook<RestrictedItemsSections[]>(
  "restrictedItemsSections",
  `${centralizedHost}/api/restricted/item/section-one`
);
const useRestrictedItemsSectionsList = createDataHooksForPages<GuidelinesForCheckedinLuggageSection2[]>(
  "restrictedItemsSectionsList",
  (listId) => `${centralizedHost}/api/restricted/item/section-two/${listId}`
);
const useLagsSection3 = createDataHook<LagsSection3[]>("lagsSection3", `${centralizedHost}/api/lags/section-three`);
const useLagsSection4 = createDataHook<LagsSection4[]>("lagsSection4", `${centralizedHost}/api/lags/section-four`);

const useLagsGuidelineSection = createDataHooksForPages<LagsGuidelineSection[]>(
  "lagsGuidelineSection",
  (listId) => `${centralizedHost}/api/lags-test-steps/all-fields/${listId}`
);

const useLagsSteps = createDataHooksForPages<LagsSteps[]>(
  "lagsSteps",
  (listId) => `${centralizedHost}/api/lags-steps/all-fields/${listId}`
);

export {
  useGuidelinesForCheckedinLuggageSection1,
  useGuidelinesForCheckedinLuggageSection2,
  useLagsGuidelineSection,
  useLagsSection1,
  useLagsSection2,
  useLagsSection3,
  useLagsSection4,
  useLagsSteps,
  useRestrictedItemsSections,
  useRestrictedItemsSectionsList,
  useSingleCabinLuggagePolicySection1,
  useSingleCabinLuggagePolicySection2,
};
